import {
  ArrowBackIos as BackIcon,
  ArrowForwardIos as ForwardIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
} from "@mui/material";
import AddAppointmentModal from "components/features/appointment/AddAppointmentModal";
import EditAppointmentModal from "components/features/appointment/EditAppointmentModal";
import DashboardLayout from "components/layouts/DashboardLayout";
import { useToast } from "contexts/ToastContext";
import {
  format,
  startOfWeek,
  addDays,
  addHours,
  isSameDay,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { endOfTheDay, startOfTheDay } from "helpers/dateTimeHelper";
import { AppointmentData, useAppointments } from "hooks/useAppointments";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { themeColors } from "theme/colorTokens";

import AppointmentDayView from "./components/Appointment/AppointmentDayView";
import AppointmentMonthView from "./components/Appointment/AppointmentMonthView";
import AppointmentWeekView from "./components/Appointment/AppointmentWeekView";

export enum AppointViewType {
  Day = "day",
  Week = "week",
  Month = "month",
  Year = "year",
}

enum CalendarDirection {
  Prev = "prev",
  Next = "next",
}

const AppointmentPage: React.FC = () => {
  // Always start with the current week
  const [currentDate, setCurrentDate] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 0 })
  );
  const [viewType, setViewType] = useState<AppointViewType>(
    AppointViewType.Week
  );
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] =
    useState<AppointmentData | null>(null);

  // Use the appointments hook
  const { appointments, loading, error, fetchAppointments, getColorByCode } =
    useAppointments();

  // Toast context for error handling
  const { showError } = useToast();

  const handleViewChange = (newView: AppointViewType) => {
    setViewType(newView);
    const now = new Date();
    switch (newView) {
      case AppointViewType.Day:
        setCurrentDate(now);
        break;
      case AppointViewType.Week:
        setCurrentDate(startOfWeek(now, { weekStartsOn: 0 }));
        break;
      case AppointViewType.Month:
        setCurrentDate(startOfMonth(now));
        break;
      case AppointViewType.Year:
        setCurrentDate(new Date(now.getFullYear(), 0, 1));
        break;
      default:
        break;
    }
  };

  const handleGoToCurrentWeek = () => {
    const now = new Date();
    switch (viewType) {
      case AppointViewType.Day:
        setCurrentDate(now);
        break;
      case AppointViewType.Week:
        setCurrentDate(startOfWeek(now, { weekStartsOn: 0 }));
        break;
      case AppointViewType.Month:
        setCurrentDate(startOfMonth(now));
        break;
      case AppointViewType.Year:
        setCurrentDate(new Date(now.getFullYear(), 0, 1));
        break;
      default:
        setCurrentDate(startOfWeek(now, { weekStartsOn: 0 }));
        break;
    }
  };

  useEffect(() => {
    if (error) {
      showError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleDateChange = (direction: CalendarDirection) => {
    const newDate = new Date(currentDate);

    switch (viewType) {
      case AppointViewType.Day:
        newDate.setDate(
          currentDate.getDate() +
            (direction === CalendarDirection.Next ? 1 : -1)
        );
        break;
      case AppointViewType.Week:
        newDate.setDate(
          currentDate.getDate() +
            (direction === CalendarDirection.Next ? 7 : -7)
        );
        break;
      case AppointViewType.Month:
        newDate.setMonth(
          currentDate.getMonth() +
            (direction === CalendarDirection.Next ? 1 : -1)
        );
        break;
      case AppointViewType.Year:
        newDate.setFullYear(
          currentDate.getFullYear() +
            (direction === CalendarDirection.Next ? 1 : -1)
        );
        break;
    }

    setCurrentDate(newDate);
  };

  // Fetch appointments when date range changes
  useEffect(() => {
    const getDateRange = () => {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const yearStart = new Date(currentDate.getFullYear(), 0, 1);
      const yearEnd = new Date(currentDate.getFullYear(), 11, 31);
      const weekEnd = addDays(weekStart, 6);
      switch (viewType) {
        case AppointViewType.Day:
          return {
            startDate: startOfTheDay(
              currentDate.getDay(),
              currentDate.getFullYear(),
              currentDate.getMonth()
            ).toDate(),
            endDate: endOfTheDay(
              currentDate.getDay(),
              currentDate.getFullYear(),
              currentDate.getMonth()
            ).toDate(),
          };
        case AppointViewType.Week:
          return { startDate: weekStart, endDate: weekEnd };
        case AppointViewType.Month:
          return {
            startDate: startOfMonth(currentDate),
            endDate: endOfMonth(currentDate),
          };
        case AppointViewType.Year:
          return { startDate: yearStart, endDate: yearEnd };
        default:
          return {
            startDate: startOfMonth(currentDate),
            endDate: endOfMonth(currentDate),
          };
      }
    };

    const { startDate, endDate } = getDateRange();
    fetchAppointments(startDate, endDate);
  }, [currentDate, viewType, fetchAppointments]);

  const timeSlots = Array.from({ length: 24 }, (_, i) =>
    addHours(
      new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
        0
      ),
      i
    )
  );

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 }); // Sunday

  const renderCalendarView = () => {
    switch (viewType) {
      case AppointViewType.Day:
        return renderDayView();
      case AppointViewType.Week:
        return renderWeekView();
      case AppointViewType.Month:
        return renderMonthView();
      case AppointViewType.Year:
        return renderYearView();
      default:
        return renderWeekView();
    }
  };

  const renderDayView = () => (
    <AppointmentDayView
      currentDate={currentDate}
      timeSlots={timeSlots}
      getAppointmentsForDay={getAppointmentsForDay}
      getAppointmentStyle={getAppointmentStyle}
      handleEditAppointment={handleEditAppointment}
    />
  );

  const renderMonthView = () => (
    <AppointmentMonthView
      currentDate={currentDate}
      getAppointmentsForDay={getAppointmentsForDay}
      getColorByCode={getColorByCode}
      handleEditAppointment={handleEditAppointment}
    />
  );

  const renderYearView = () => (
    <Box className="p-4">
      <Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>
        Year view - Coming soon
      </Typography>
    </Box>
  );

  // Get appointments for a specific day
  const getAppointmentsForDay = useCallback(
    (date: Date) => {
      return appointments.filter(
        (appointment) =>
          appointment.startDate && isSameDay(appointment.startDate, date)
      );
    },
    [appointments]
  );

  const handleEditAppointment = useCallback(
    (appointment: AppointmentData) => {
      setEditingAppointment(appointment);
      setIsEditModalOpen(true);
    },
    [setEditingAppointment, setIsEditModalOpen]
  );

  const handleCloseModal = useCallback(
    (isRefresh: boolean = false) => {
      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
      setEditingAppointment(null);
      if (isRefresh) {
        fetchAppointments();
      }
    },
    [
      setIsAddModalOpen,
      setIsEditModalOpen,
      setEditingAppointment,
      fetchAppointments,
    ]
  );

  const getAppointmentStyle = useCallback(
    (appointment: AppointmentData) => {
      if (!appointment.startTime || !appointment.endTime) {
        return {
          top: "0px",
          height: "20px",
          backgroundColor: getColorByCode(appointment.colorCode),
          bottom: "1px",
          zIndex: 1,
        };
      }

      const startHour = appointment.startTime.getHours();
      const startMinute = appointment.startTime.getMinutes();
      const endHour = appointment.endTime.getHours();
      const endMinute = appointment.endTime.getMinutes();

      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      const durationInMinutes = endTimeInMinutes - startTimeInMinutes;

      // Each hour slot is 60px, so each minute is 1px
      const top = startMinute + 30;
      const height = Math.max(durationInMinutes * 1, 20); // Minimum height of 20px

      return {
        top: `${top + 0.5}px`,
        height: `${height - 1}px`,
        backgroundColor: getColorByCode(appointment.colorCode),
        bottom: "0.5px",
        zIndex: 1,
      };
    },
    [getColorByCode]
  );

  const renderWeekView = useCallback(
    () => (
      <AppointmentWeekView
        currentDate={currentDate}
        timeSlots={timeSlots}
        getAppointmentStyle={getAppointmentStyle}
        getAppointmentsForDay={getAppointmentsForDay}
        handleEditAppointment={handleEditAppointment}
      />
    ),
    [
      currentDate,
      timeSlots,
      getAppointmentStyle,
      getAppointmentsForDay,
      handleEditAppointment,
    ]
  );

  const formattedHeaderDate = useMemo(() => {
    switch (viewType) {
      case AppointViewType.Day:
        return format(currentDate, "EEEE, MMMM d, yyyy");
      case AppointViewType.Week:
        return `${format(weekStart, "MMM d")} - ${format(addDays(weekStart, 6), "MMM d, yyyy")}`;
      case AppointViewType.Month:
        return format(currentDate, "MMMM yyyy");
      case AppointViewType.Year:
        return format(currentDate, "yyyy");
      default:
        return format(currentDate, "MMMM yyyy");
    }
  }, [currentDate, viewType, weekStart]);

  return (
    <DashboardLayout
      title="Appointment"
      headerActions={
        <Button
          variant="contained"
          onClick={() => setIsAddModalOpen(true)}
          sx={{
            backgroundColor: themeColors.primary.main,
            borderRadius: "50px",
            padding: "8px 16px",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            width: "80px",
            "&:hover": {
              backgroundColor: themeColors.primary.dark,
            },
          }}
        >
          Add
        </Button>
      }
    >
      {/* View Toggle Buttons and Navigation */}
      <Box className="px-4 py-2">
        <Box className="flex items-center justify-between">
          <Box className="flex items-center gap-2">
            <Button
              variant="outlined"
              onClick={handleGoToCurrentWeek}
              sx={{
                borderColor: themeColors.border.light,
                color: themeColors.text.primary,
                backgroundColor: themeColors.background.default,
                borderRadius: "20px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                marginLeft: "8px",
                "&:hover": {
                  borderColor: themeColors.border.main,
                  backgroundColor: themeColors.background.secondary,
                },
              }}
            >
              Today
            </Button>
            <IconButton
              onClick={() => handleDateChange(CalendarDirection.Prev)}
              sx={{ color: themeColors.text.secondary }}
            >
              <BackIcon />
            </IconButton>

            <IconButton
              onClick={() => handleDateChange(CalendarDirection.Next)}
              sx={{ color: themeColors.text.secondary }}
            >
              <ForwardIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                color: themeColors.text.primary,
                minWidth: "200px",
                textAlign: "center",
              }}
            >
              {formattedHeaderDate}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              backgroundColor: themeColors.background.default,
              padding: "4px",
              borderRadius: "20px",
              border: `1px solid ${themeColors.border.light}`,
            }}
          >
            {["Day", "Week", "Month", "Year"].map((view) => (
              <Button
                key={view}
                onClick={() =>
                  handleViewChange(view.toLowerCase() as AppointViewType)
                }
                sx={{
                  flex: 1,
                  minWidth: "60px",
                  height: "36px",
                  borderRadius: "16px",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  backgroundColor:
                    viewType === view.toLowerCase()
                      ? themeColors.background.highlight
                      : "transparent",
                  color:
                    viewType === view.toLowerCase()
                      ? themeColors.text.blueHighlight
                      : themeColors.text.secondary,
                  border: "none",
                  "&:hover": {
                    backgroundColor:
                      viewType === view.toLowerCase()
                        ? themeColors.background.highlight
                        : themeColors.background.secondary,
                  },
                }}
              >
                {view}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Calendar Content */}
      <Box
        className="flex-1 p-4 bg-white"
        sx={{
          height: "calc(100vh - 170px)",
          overflow: "hidden",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: 0,
          }}
        >
          {/* Loading State */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && (
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: themeColors.border.light,
                  borderRadius: "3px",
                  opacity: 0.6,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  opacity: 1,
                  background: themeColors.text.secondary,
                },
              }}
              ref={(el) => {
                if (el && el instanceof HTMLElement) {
                  el.scrollTop = 480;
                }
              }}
            >
              {renderCalendarView()}
            </Box>
          )}
        </Paper>
      </Box>

      <AddAppointmentModal open={isAddModalOpen} onClose={handleCloseModal} />

      <EditAppointmentModal
        open={isEditModalOpen}
        onClose={handleCloseModal}
        appointment={editingAppointment}
      />
    </DashboardLayout>
  );
};

export default AppointmentPage;
